import React, { useEffect, useState, useCallback } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { api, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { APP_VERSION, BUILD_DATE } from "@/lib/version";
import { getStoredTheme, setTheme } from "@/lib/theme";
import {
  User as UserIcon, Bell, Lock, Shield, Sun, RefreshCw, Trash2, Settings as SettingsIcon,
  Phone, Mail, Upload, MonitorCog, ShieldCheck, AlertTriangle, Building, Globe,
} from "lucide-react";

const TABS_BASE = [
  { id: "profile",        label: "Profile",        icon: UserIcon },
  { id: "notifications",  label: "Notifications",  icon: Bell },
  { id: "password",       label: "Password",       icon: Lock },
  { id: "security",       label: "Security",       icon: Shield },
  { id: "theme",          label: "Theme",          icon: Sun },
  { id: "reset",          label: "Reset / Update", icon: RefreshCw },
  { id: "delete",         label: "Delete Account", icon: Trash2 },
];
const ADMIN_TABS = [
  { id: "app-settings",   label: "App Settings",   icon: MonitorCog },
  { id: "branding",       label: "Branding",       icon: Building },
];

export default function Settings() {
  const { user } = useAuth();
  const [active, setActive] = useState("profile");
  const tabs = user?.role === "admin" ? [...TABS_BASE, ...ADMIN_TABS] : TABS_BASE;

  return (
    <DashboardLayout>
      <p className="eyebrow">Account</p>
      <h1 className="font-display text-3xl font-semibold mt-2 flex items-center gap-2">
        <SettingsIcon size={24} className="text-[var(--adn-gold)]" /> Settings
      </h1>
      <p className="text-sm text-[var(--adn-text-soft)] mt-1">
        Manage your account, preferences, and app behavior.
      </p>

      <div className="mt-8 grid md:grid-cols-[220px_1fr] gap-8 max-w-5xl">
        {/* Sidebar tabs */}
        <nav className="space-y-1" data-testid="settings-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              data-testid={`settings-tab-${t.id}`}
              onClick={() => setActive(t.id)}
              className={`w-full text-left px-3 py-2.5 text-sm flex items-center gap-2.5 border-l-2 transition-colors ${
                active === t.id
                  ? "border-[var(--adn-gold)] bg-white text-[var(--adn-navy)] font-semibold"
                  : "border-transparent text-[var(--adn-text-soft)] hover:bg-white hover:text-[var(--adn-navy)]"
              }`}
            >
              <t.icon size={15} /> <span>{t.label}</span>
            </button>
          ))}
        </nav>

        {/* Panel */}
        <section className="min-w-0">
          {active === "profile" && <ProfilePanel />}
          {active === "notifications" && <NotificationsPanel />}
          {active === "password" && <PasswordPanel />}
          {active === "security" && <SecurityPanel />}
          {active === "theme" && <ThemePanel />}
          {active === "reset" && <ResetPanel />}
          {active === "delete" && <DeletePanel />}
          {active === "app-settings" && <AppSettingsPanel />}
          {active === "branding" && <BrandingPanel />}
        </section>
      </div>

      <style>{`
        .p-input { width: 100%; background: white; border: 1px solid #CBD5E1; padding: 10px 12px; font-size: 14px; }
        .p-input:focus { outline: none; border-color: var(--adn-navy); }
        .toggle { width: 40px; height: 22px; border-radius: 9999px; position: relative; transition: background 200ms ease; }
        .toggle::after { content: ""; position: absolute; top: 2px; left: 2px; width: 18px; height: 18px; border-radius: 50%; background: white; transition: transform 200ms ease; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }
        .toggle.on { background: var(--adn-emerald); }
        .toggle.off { background: #CBD5E1; }
        .toggle.on::after { transform: translateX(18px); }
      `}</style>
    </DashboardLayout>
  );
}

/* ---------------- Profile ---------------- */
function ProfilePanel() {
  const { user, setUser } = useAuth();
  const [f, setF] = useState({
    name: user?.name || "",
    phone: user?.phone || "",
    contact_email: user?.contact_email || "",
    avatar_url: user?.avatar_url || "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const uploadAvatar = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setF((x) => ({ ...x, avatar_url: data.url }));
      toast.success("Photo uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", {
        name: f.name || null,
        phone: f.phone || null,
        contact_email: f.contact_email || null,
        avatar_url: f.avatar_url || null,
      });
      setUser(data);
      toast.success("Profile updated");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Card title="Profile" subtitle="Your basic identity inside the marketplace.">
      <div className="flex items-center gap-5 mb-6">
        <div className="h-20 w-20 rounded-full bg-[var(--adn-cream)] border border-[var(--adn-line)] overflow-hidden flex items-center justify-center text-[var(--adn-text-muted)]">
          {f.avatar_url
            ? <img src={f.avatar_url} alt="" className="h-full w-full object-cover" />
            : <UserIcon size={28} />}
        </div>
        <label className="cursor-pointer btn-outline-navy px-4 py-2 text-sm rounded inline-flex items-center gap-2">
          <Upload size={14} />
          <span>{uploading ? "Uploading…" : "Upload photo"}</span>
          <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="settings-avatar-upload" />
        </label>
        <div className="text-xs text-[var(--adn-text-muted)]">
          <div className="font-semibold uppercase tracking-wider mb-1">Role</div>
          <div className="font-mono text-[var(--adn-navy)]">{user?.role || "—"}</div>
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Display name">
          <input className="p-input" value={f.name} onChange={(e)=>setF({...f, name: e.target.value})} data-testid="settings-name" />
        </Field>
        <Field label="Account email">
          <input className="p-input" value={user?.email || ""} disabled />
        </Field>
        <Field label="Phone" icon={Phone}>
          <input className="p-input pl-9" value={f.phone} onChange={(e)=>setF({...f, phone: e.target.value})} data-testid="settings-phone" />
        </Field>
        <Field label="Public contact email" icon={Mail}>
          <input className="p-input pl-9" value={f.contact_email} onChange={(e)=>setF({...f, contact_email: e.target.value})} />
        </Field>
      </div>

      <div className="mt-6">
        <button data-testid="settings-save-profile" onClick={save} disabled={saving} className="btn-gold px-5 py-2.5 rounded text-sm">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </Card>
  );
}

/* ---------------- Notifications ---------------- */
function NotificationsPanel() {
  const { user, setUser } = useAuth();
  const [prefs, setPrefs] = useState({
    notif_email_offers:   user?.notif_email_offers   ?? true,
    notif_email_messages: user?.notif_email_messages ?? true,
    notif_email_status:   user?.notif_email_status   ?? true,
    notif_sms_offers:     user?.notif_sms_offers     ?? false,
    notif_sms_messages:   user?.notif_sms_messages   ?? false,
    notif_sms_status:     user?.notif_sms_status     ?? false,
  });
  const [saving, setSaving] = useState(false);

  const toggle = (k) => setPrefs((p) => ({ ...p, [k]: !p[k] }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/auth/profile", prefs);
      setUser(data);
      toast.success("Notification preferences saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  return (
    <Card title="Notifications" subtitle="Choose how you'd like to hear from the marketplace.">
      <Section title="Email" hint="We use email for offer notifications, messages, and status changes.">
        <Toggle label="New offers" k="notif_email_offers" v={prefs.notif_email_offers} onToggle={toggle} />
        <Toggle label="New messages" k="notif_email_messages" v={prefs.notif_email_messages} onToggle={toggle} />
        <Toggle label="Status changes" k="notif_email_status" v={prefs.notif_email_status} onToggle={toggle} />
      </Section>
      <Section title="SMS" hint="Requires a verified phone number on your profile.">
        <Toggle label="New offers" k="notif_sms_offers" v={prefs.notif_sms_offers} onToggle={toggle} />
        <Toggle label="New messages" k="notif_sms_messages" v={prefs.notif_sms_messages} onToggle={toggle} />
        <Toggle label="Status changes" k="notif_sms_status" v={prefs.notif_sms_status} onToggle={toggle} />
        <p className="text-[11px] text-[var(--adn-text-muted)] mt-3">
          SMS dispatch is not yet wired — preferences will take effect when SMS goes live.
        </p>
      </Section>
      <div className="mt-2">
        <button data-testid="settings-save-notifs" onClick={save} disabled={saving} className="btn-gold px-5 py-2.5 rounded text-sm">
          {saving ? "Saving…" : "Save preferences"}
        </button>
      </div>
    </Card>
  );
}

/* ---------------- Password ---------------- */
function PasswordPanel() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e?.preventDefault?.();
    if (next.length < 8) { toast.error("New password must be at least 8 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: next });
      toast.success("Password updated");
      setCur(""); setNext("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not update password"); }
    finally { setBusy(false); }
  };
  return (
    <Card title="Password" subtitle="Set a new password for your account.">
      <form onSubmit={submit} className="space-y-4 max-w-md">
        <Field label="Current password">
          <input type="password" autoComplete="current-password" className="p-input"
            value={cur} onChange={(e)=>setCur(e.target.value)} data-testid="settings-pw-cur" />
        </Field>
        <Field label="New password (min 8)">
          <input type="password" autoComplete="new-password" className="p-input"
            value={next} onChange={(e)=>setNext(e.target.value)} data-testid="settings-pw-new" />
        </Field>
        <button data-testid="settings-pw-submit" type="submit" disabled={busy || !next} className="btn-outline-navy px-5 py-2.5 rounded text-sm">
          {busy ? "Saving…" : "Update password"}
        </button>
        <p className="text-[11px] text-[var(--adn-text-muted)]">
          If you signed up with Google, leave current password blank to set one for the first time.
        </p>
      </form>
    </Card>
  );
}

/* ---------------- Security: Sessions + 2FA toggle ---------------- */
function SecurityPanel() {
  const { user, setUser } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [twoFa, setTwoFa] = useState(user?.two_factor_enabled || false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/auth/sessions"); setSessions(data.sessions || []); }
    catch { /* ignore */ } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const revoke = async (token) => {
    if (!window.confirm("Sign out this session?")) return;
    try { await api.post("/auth/sessions/revoke", { session_token: token }); toast.success("Session revoked"); load(); }
    catch { toast.error("Could not revoke"); }
  };
  const revokeOthers = async () => {
    if (!window.confirm("Sign out all other devices?")) return;
    try { const { data } = await api.post("/auth/sessions/revoke-others"); toast.success(`Signed out ${data.deleted} session(s)`); load(); }
    catch { toast.error("Failed"); }
  };
  const flip2FA = async () => {
    const newVal = !twoFa;
    setTwoFa(newVal);
    try { const { data } = await api.put("/auth/profile", { two_factor_enabled: newVal }); setUser(data); toast.success(newVal ? "2FA preference saved (rollout coming)" : "2FA disabled"); }
    catch { toast.error("Could not save"); setTwoFa(!newVal); }
  };

  return (
    <Card title="Security" subtitle="Manage active sessions and login security.">
      <Section title="Two-factor authentication" hint="Adds a one-time code to your sign-ins. Email-based 2FA rolls out soon — toggle to opt in early.">
        <div className="flex items-center justify-between bg-[var(--adn-cream)] border border-[var(--adn-line)] px-4 py-3">
          <div className="flex items-center gap-3">
            <ShieldCheck size={18} className="text-[var(--adn-emerald)]" />
            <div>
              <div className="text-sm font-semibold">Email 2FA</div>
              <div className="text-[11px] text-[var(--adn-text-muted)] mt-0.5">{twoFa ? "Enabled (will activate at launch)" : "Disabled"}</div>
            </div>
          </div>
          <button onClick={flip2FA} className={`toggle ${twoFa ? "on" : "off"}`} aria-label="Toggle 2FA" data-testid="settings-2fa-toggle" />
        </div>
      </Section>

      <Section title="Active sessions" hint="Every device currently signed in with your account.">
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs text-[var(--adn-text-muted)]">{loading ? "Loading…" : `${sessions.length} session(s)`}</span>
          <button onClick={revokeOthers} className="text-xs underline text-rose-600 hover:text-rose-700" data-testid="settings-revoke-others">
            Sign out all other devices
          </button>
        </div>
        <div className="border border-[var(--adn-line)] divide-y divide-[var(--adn-line)] bg-white">
          {sessions.length === 0 && !loading && (
            <div className="p-4 text-sm text-[var(--adn-text-soft)]">No active sessions.</div>
          )}
          {sessions.map((s) => (
            <div key={s._id} className="px-4 py-3 flex items-start justify-between gap-3" data-testid="settings-session-row">
              <div className="min-w-0">
                <div className="text-sm font-medium flex items-center gap-2">
                  {s.is_current && <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 px-1.5 py-0.5 rounded">This device</span>}
                  <span className="font-mono text-xs text-[var(--adn-text-soft)]">{s.session_token_preview}</span>
                </div>
                <div className="text-[11px] text-[var(--adn-text-muted)] mt-1">
                  {s.created_at && <span>Signed in {fmtDate(s.created_at)} · </span>}
                  <span className="font-mono">{s.ip || "—"}</span>
                </div>
                {s.user_agent && (
                  <div className="text-[11px] text-[var(--adn-text-muted)] mt-0.5 truncate max-w-md">{s.user_agent}</div>
                )}
              </div>
              {!s.is_current && (
                <button onClick={() => revoke(s._id)} className="text-xs underline text-rose-600 hover:text-rose-700">Revoke</button>
              )}
            </div>
          ))}
        </div>
      </Section>
    </Card>
  );
}

/* ---------------- Theme ---------------- */
function ThemePanel() {
  const [pref, setPref] = useState(getStoredTheme());
  const apply = (val) => { setPref(val); setTheme(val); toast.success(`Theme set to ${val}`); };
  const options = [
    { id: "light",  label: "Light",  desc: "Default cream + navy palette." },
    { id: "dark",   label: "Dark",   desc: "Deep navy background. Beta." },
    { id: "system", label: "System", desc: "Match your device preference." },
  ];
  return (
    <Card title="Theme" subtitle="Choose a light or dark interface. Your choice is saved on this device.">
      <div className="grid sm:grid-cols-3 gap-3">
        {options.map((o) => (
          <button
            key={o.id}
            onClick={() => apply(o.id)}
            data-testid={`settings-theme-${o.id}`}
            className={`text-left p-4 border ${pref === o.id ? "border-[var(--adn-gold)] bg-white" : "border-[var(--adn-line)] bg-white hover:border-[var(--adn-navy)]"}`}
          >
            <div className="text-sm font-semibold">{o.label}</div>
            <div className="text-[11px] text-[var(--adn-text-muted)] mt-1">{o.desc}</div>
            {pref === o.id && <div className="mt-3 text-[10px] uppercase tracking-wider text-[var(--adn-gold)] font-bold">Selected</div>}
          </button>
        ))}
      </div>
    </Card>
  );
}

/* ---------------- Reset / Update ---------------- */
function ResetPanel() {
  const [busy, setBusy] = useState(false);
  const [latest, setLatest] = useState(null);

  const checkUpdate = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/app-settings/public");
      setLatest(data.version);
      if (data.version === APP_VERSION) toast.success(`You're on the latest version (${APP_VERSION})`);
      else toast.success(`New version available: ${data.version}. Reload to update.`);
    } catch { toast.error("Couldn't check for updates"); }
    finally { setBusy(false); }
  };

  const hardRefresh = async () => {
    if (!window.confirm("This will clear the offline cache and reload the app. Continue?")) return;
    try {
      if ("serviceWorker" in navigator) {
        const regs = await navigator.serviceWorker.getRegistrations();
        for (const r of regs) {
          try { await r.unregister(); } catch { /* ignore */ }
        }
      }
      if (window.caches) {
        const names = await caches.keys();
        await Promise.all(names.map((n) => caches.delete(n)));
      }
    } catch { /* ignore */ }
    // hard reload
    window.location.reload(true);
  };

  const clearLocal = () => {
    if (!window.confirm("Clear local app preferences on this device? (Theme, install prompts) Your account is not affected.")) return;
    try {
      Object.keys(localStorage).forEach((k) => { if (k.startsWith("adn:") || k.startsWith("install:")) localStorage.removeItem(k); });
      sessionStorage.clear();
      toast.success("Local preferences cleared");
    } catch { toast.error("Could not clear storage"); }
  };

  return (
    <Card title="Reset / Update app" subtitle="If the app feels stale after a deploy, use these tools to refresh it.">
      <div className="bg-[var(--adn-cream)] border border-[var(--adn-line)] p-4 mb-5 text-sm">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-[10px] uppercase tracking-[0.12em] font-bold text-[var(--adn-text-muted)]">Installed version</span>
          <span className="font-mono">{APP_VERSION}</span>
          <span className="text-[var(--adn-text-muted)]">·</span>
          <span className="text-[var(--adn-text-muted)]">build {BUILD_DATE}</span>
          {latest && latest !== APP_VERSION && (
            <span className="ml-2 text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 px-2 py-0.5 rounded font-bold">
              Update available → {latest}
            </span>
          )}
        </div>
      </div>

      <div className="space-y-3">
        <ActionRow
          title="Check for updates"
          desc="Compare your local version against the latest deployed."
          btn="Check now"
          onClick={checkUpdate}
          loading={busy}
          testid="settings-check-update"
          icon={RefreshCw}
        />
        <ActionRow
          title="Reset cache & reload"
          desc="Unregisters the service worker and clears offline caches, then reloads."
          btn="Reset & reload"
          onClick={hardRefresh}
          danger
          testid="settings-hard-refresh"
          icon={AlertTriangle}
        />
        <ActionRow
          title="Clear local preferences"
          desc="Removes theme + install-prompt flags on this device. Your account is safe."
          btn="Clear"
          onClick={clearLocal}
          testid="settings-clear-local"
          icon={Trash2}
        />
      </div>
    </Card>
  );
}

/* ---------------- Delete account ---------------- */
function DeletePanel() {
  const { user } = useAuth();
  const [confirm, setConfirm] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (confirm !== "DELETE") { toast.error("Type DELETE to confirm"); return; }
    if (!window.confirm("This permanently deactivates your account and signs you out. Continue?")) return;
    setBusy(true);
    try {
      await api.post("/auth/delete-account", { confirm, password: pw || undefined });
      toast.success("Account deleted");
      setTimeout(() => { window.location.href = "/"; }, 800);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not delete account"); }
    finally { setBusy(false); }
  };

  if (user?.role === "admin") {
    return (
      <Card title="Delete account" subtitle="Admin accounts cannot self-delete.">
        <p className="text-sm text-[var(--adn-text-soft)]">
          For safety, admin accounts must be removed by another admin from the User management panel.
        </p>
      </Card>
    );
  }

  return (
    <Card title="Delete account" subtitle="Permanently deactivate and anonymize your ADN account." danger>
      <div className="bg-rose-50 border border-rose-200 p-4 mb-5 text-sm text-rose-900">
        <div className="font-semibold mb-1 flex items-center gap-2"><AlertTriangle size={15} /> This action cannot be undone</div>
        <ul className="list-disc pl-5 text-[13px] space-y-0.5">
          <li>Your sessions will be terminated everywhere.</li>
          <li>Your name, email, and contact info will be anonymized.</li>
          <li>Properties, offers, and messages remain visible to admins for compliance.</li>
        </ul>
      </div>
      <div className="max-w-md space-y-4">
        <Field label='Type "DELETE" to confirm'>
          <input className="p-input" value={confirm} onChange={(e)=>setConfirm(e.target.value)} placeholder="DELETE" data-testid="settings-delete-confirm" />
        </Field>
        <Field label="Password (if you have one set)">
          <input type="password" className="p-input" value={pw} onChange={(e)=>setPw(e.target.value)} data-testid="settings-delete-pw" />
        </Field>
        <button onClick={submit} disabled={busy || confirm !== "DELETE"} data-testid="settings-delete-submit"
          className="bg-rose-600 hover:bg-rose-700 disabled:opacity-50 text-white px-5 py-2.5 text-sm rounded">
          {busy ? "Deleting…" : "Delete my account"}
        </button>
      </div>
    </Card>
  );
}

/* ---------------- Admin: App Settings ---------------- */
function AppSettingsPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/admin/app-settings"); setS(data); }
    catch { toast.error("Could not load app settings"); }
  })(); }, []);

  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/admin/app-settings", s); setS(data); toast.success("App settings saved"); }
    catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!s) return <Card title="App Settings" subtitle="Loading…"><div className="text-sm text-[var(--adn-text-soft)]">Loading…</div></Card>;

  return (
    <Card title="App Settings" subtitle="Global controls for the marketplace. Admin only.">
      <Section title="Access">
        <ToggleBig label="Maintenance mode" desc="Show a maintenance banner to all visitors."
          v={!!s.maintenance_mode} onToggle={() => setS({ ...s, maintenance_mode: !s.maintenance_mode })} testid="app-set-maint" />
        <ToggleBig label="Allow new signups" desc="When off, the registration form is hidden."
          v={!!s.allow_signups} onToggle={() => setS({ ...s, allow_signups: !s.allow_signups })} testid="app-set-signups" />
      </Section>

      <Section title="Pricing">
        <Field label="Default lead fee (USD)">
          <input className="p-input" type="number" value={s.default_lead_fee ?? 0}
            onChange={(e)=>setS({ ...s, default_lead_fee: parseFloat(e.target.value || "0") })} data-testid="app-set-fee" />
        </Field>
      </Section>

      <Section title="Footer contact" hint="Shown on the public-site footer.">
        <div className="grid md:grid-cols-2 gap-4">
          <Field label="Phone" icon={Phone}>
            <input className="p-input pl-9" value={s.footer_phone || ""} onChange={(e)=>setS({...s, footer_phone: e.target.value})} placeholder="(555) 555-0100" />
          </Field>
          <Field label="Email" icon={Mail}>
            <input className="p-input pl-9" value={s.footer_email || ""} onChange={(e)=>setS({...s, footer_email: e.target.value})} placeholder="hello@adn-marketplace.com" />
          </Field>
          <Field label="Address" icon={Globe} full>
            <input className="p-input pl-9" value={s.footer_address || ""} onChange={(e)=>setS({...s, footer_address: e.target.value})} placeholder="Suite, City, State" />
          </Field>
        </div>
      </Section>

      <Section title="Announcement banner" hint="Optional — shown across all pages when set.">
        <Field label="Banner text">
          <input className="p-input" value={s.announcement || ""} onChange={(e)=>setS({...s, announcement: e.target.value})} placeholder="e.g. Now serving Dallas/Fort Worth" />
        </Field>
      </Section>

      <button onClick={save} disabled={saving} data-testid="app-set-save" className="btn-gold px-5 py-2.5 rounded text-sm">
        {saving ? "Saving…" : "Save app settings"}
      </button>
    </Card>
  );
}

/* ---------------- Admin: Branding ---------------- */
function BrandingPanel() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => { (async () => {
    try { const { data } = await api.get("/admin/app-settings"); setS(data); }
    catch { toast.error("Could not load branding"); }
  })(); }, []);

  const upload = async (e, key) => {
    const file = e.target.files?.[0]; if (!file) return;
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setS((x) => ({ ...x, [key]: data.url }));
      toast.success("Uploaded");
    } catch { toast.error("Upload failed"); }
    finally { e.target.value = ""; }
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/app-settings", {
        brand_logo_url: s.brand_logo_url || null,
        brand_splash_url: s.brand_splash_url || null,
        brand_primary_color: s.brand_primary_color || null,
      });
      setS(data); toast.success("Branding saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  if (!s) return <Card title="Branding" subtitle="Loading…"><div className="text-sm text-[var(--adn-text-soft)]">Loading…</div></Card>;

  return (
    <Card title="Branding" subtitle="Upload your logo, splash image, and pick a primary color. Admin only.">
      <Section title="Logo">
        <BrandUpload label="App logo" url={s.brand_logo_url} k="brand_logo_url" onUpload={(e)=>upload(e,"brand_logo_url")} onClear={()=>setS({...s, brand_logo_url: null})} />
      </Section>
      <Section title="Splash image" hint="Shown on the loading screen of the PWA.">
        <BrandUpload label="Splash image" url={s.brand_splash_url} k="brand_splash_url" onUpload={(e)=>upload(e,"brand_splash_url")} onClear={()=>setS({...s, brand_splash_url: null})} />
      </Section>
      <Section title="Primary color" hint="Used for buttons, links, and accents.">
        <div className="flex items-center gap-3">
          <input type="color" className="h-10 w-14 border border-[var(--adn-line)] p-0 cursor-pointer"
            value={s.brand_primary_color || "#D4AF37"} onChange={(e)=>setS({...s, brand_primary_color: e.target.value})} />
          <input className="p-input max-w-[160px] font-mono" value={s.brand_primary_color || ""} onChange={(e)=>setS({...s, brand_primary_color: e.target.value})} placeholder="#D4AF37" />
        </div>
      </Section>
      <button onClick={save} disabled={saving} data-testid="branding-save" className="btn-gold px-5 py-2.5 rounded text-sm">
        {saving ? "Saving…" : "Save branding"}
      </button>
    </Card>
  );
}

/* ---------------- Small reusable bits ---------------- */
function Card({ title, subtitle, children, danger }) {
  return (
    <div className={`bg-white border ${danger ? "border-rose-200" : "border-[var(--adn-line)]"} p-6`}>
      <h2 className={`font-display text-xl font-semibold ${danger ? "text-rose-700" : "text-[var(--adn-navy)]"}`}>{title}</h2>
      {subtitle && <p className="text-sm text-[var(--adn-text-soft)] mt-1 mb-5">{subtitle}</p>}
      {children}
    </div>
  );
}
function Field({ label, children, icon: Icon, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="text-xs font-semibold text-[var(--adn-text-soft)] mb-1.5 block uppercase tracking-wider">{label}</span>
      <div className="relative">
        {Icon && <Icon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--adn-text-muted)] pointer-events-none" />}
        {children}
      </div>
    </label>
  );
}
function Section({ title, hint, children }) {
  return (
    <div className="mb-6">
      <h3 className="text-xs font-bold uppercase tracking-[0.12em] text-[var(--adn-text-muted)] mb-2">{title}</h3>
      {hint && <p className="text-[12px] text-[var(--adn-text-muted)] mb-3 -mt-1">{hint}</p>}
      <div className="space-y-2">{children}</div>
    </div>
  );
}
function Toggle({ label, k, v, onToggle }) {
  return (
    <div className="flex items-center justify-between bg-[var(--adn-cream)] border border-[var(--adn-line)] px-4 py-2.5">
      <span className="text-sm">{label}</span>
      <button onClick={() => onToggle(k)} className={`toggle ${v ? "on" : "off"}`} aria-label={label} data-testid={`toggle-${k}`} />
    </div>
  );
}
function ToggleBig({ label, desc, v, onToggle, testid }) {
  return (
    <div className="flex items-center justify-between bg-[var(--adn-cream)] border border-[var(--adn-line)] px-4 py-3">
      <div>
        <div className="text-sm font-semibold">{label}</div>
        {desc && <div className="text-[11px] text-[var(--adn-text-muted)] mt-0.5">{desc}</div>}
      </div>
      <button onClick={onToggle} className={`toggle ${v ? "on" : "off"}`} aria-label={label} data-testid={testid} />
    </div>
  );
}
function ActionRow({ title, desc, btn, onClick, loading, danger, testid, icon: Icon }) {
  return (
    <div className="flex items-start justify-between gap-4 bg-white border border-[var(--adn-line)] p-4">
      <div className="min-w-0">
        <div className="text-sm font-semibold flex items-center gap-2">
          {Icon && <Icon size={14} className={danger ? "text-rose-600" : "text-[var(--adn-gold)]"} />} {title}
        </div>
        <div className="text-[12px] text-[var(--adn-text-muted)] mt-0.5">{desc}</div>
      </div>
      <button onClick={onClick} disabled={loading} data-testid={testid}
        className={`shrink-0 px-4 py-2 text-sm rounded ${danger ? "bg-rose-600 hover:bg-rose-700 text-white" : "btn-outline-navy"}`}>
        {loading ? "Working…" : btn}
      </button>
    </div>
  );
}
function BrandUpload({ url, onUpload, onClear }) {
  return (
    <div className="flex items-center gap-4">
      <div className="h-16 w-16 border border-[var(--adn-line)] bg-[var(--adn-cream)] flex items-center justify-center overflow-hidden">
        {url ? <img src={url} alt="" className="h-full w-full object-contain" /> : <span className="text-[10px] text-[var(--adn-text-muted)]">Empty</span>}
      </div>
      <label className="btn-outline-navy px-4 py-2 text-sm rounded cursor-pointer inline-flex items-center gap-2">
        <Upload size={14} /> <span>{url ? "Replace" : "Upload"}</span>
        <input type="file" accept="image/*" className="hidden" onChange={onUpload} />
      </label>
      {url && <button onClick={onClear} className="text-xs underline text-rose-600">Remove</button>}
    </div>
  );
}
